import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DynamicPipelineAnalysisOutput } from 'libs/end-to-end/src/dynamic-pipeline-analysis';
import { DynamicPipelinePhaseResultLayout } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysis.utils';
import { estimateLayoutOfPipelineAnalysis } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysis.utils';
import { gapBetweenPhases } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysis.utils';
import { gapBetweenSections } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysis.utils';
import { DynamicPipelineAnalysisSection } from 'apps/sigma2-ui/src/components/dynamic-pipeline-analysis/DynamicPipelineAnalysisSection';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import useMeasure from 'react-use-measure';

export const DynamicPipelineAnalysis = (props: {
  pipelineAnalysisOutput: DynamicPipelineAnalysisOutput;
  selectedLevel: string;
}) => {
  const { pipelineAnalysisOutput, selectedLevel } = props;

  const { myObservation } = useObservation();
  const [measureRef, measurements] = useMeasure();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    const maxScroll = scrollWidth - clientWidth;
    const progress = maxScroll > 0 ? scrollLeft / maxScroll : 0;
    setScrollProgress(progress);
  }, []);

  // Calculate right gradient width based on scroll progress
  const rightGradientWidth = useMemo(() => {
    if (
      scrollContainerRef.current?.scrollWidth ===
      scrollContainerRef.current?.clientWidth
    ) {
      return 0;
    }
    const baseWidth = 80;
    const minWidth = 0; // Minimum width when fully scrolled
    return Math.max(minWidth, baseWidth * (1 - scrollProgress));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollProgress, measurements.width]);

  // Calculate left gradient width based on scroll progress
  const leftGradientWidth = useMemo(() => {
    if (
      scrollContainerRef.current?.scrollWidth ===
      scrollContainerRef.current?.clientWidth
    ) {
      return 0;
    }
    const baseWidth = 80;
    return Math.min(baseWidth, baseWidth * scrollProgress);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollProgress, measurements.width]);

  const analysisConfig = useMemo(() => {
    const pipelineConfigs = myObservation?.pipelineAnalysisConfiguration ?? [];
    return pipelineConfigs.find(
      (config) => config.name === pipelineAnalysisOutput.name,
    );
  }, [
    pipelineAnalysisOutput.name,
    myObservation?.pipelineAnalysisConfiguration,
  ]);

  const analysisLayouts = useMemo(() => {
    return estimateLayoutOfPipelineAnalysis({
      analysisOutput: pipelineAnalysisOutput,
    });
  }, [pipelineAnalysisOutput]);

  const computedGapBetweenPhases = gapBetweenPhases(
    pipelineAnalysisOutput.phases.length + 1,
  );

  const resultHasNextPhase = useCallback(
    (layout: DynamicPipelinePhaseResultLayout) => {
      for (const phase of analysisConfig?.phases ?? []) {
        if (phase.negativeTranslation === layout.name) {
          return phase.negativeResultHasNextPhase;
        }
      }

      return true;
    },
    [analysisConfig?.phases],
  );

  const phaseLayoutsNodes = useMemo(() => {
    return analysisLayouts.map((phaseLayouts, index) => {
      const totalForPercentage = phaseLayouts.reduce(
        (acc, layout) => acc + layout.total,
        0,
      );

      return (
        <div
          className="flex flex-col flex-initial min-w-40 p-2 bg-gray-50/50 rounded border-[1px] border-gray-200"
          style={{ gap: `${gapBetweenSections}px` }}
          key={index}
        >
          {phaseLayouts.map((layout, phaseLayoutIndex) => (
            <DynamicPipelineAnalysisSection
              key={`${index}-${phaseLayoutIndex}`}
              phaseResult={layout}
              nextPhaseResults={
                resultHasNextPhase(layout) ? analysisLayouts[index + 1] : null
              }
              selectedLevel={selectedLevel}
              dimension={pipelineAnalysisOutput.dimension}
              computedGapBetweenPhases={computedGapBetweenPhases}
              totalForPercentage={totalForPercentage}
            />
          ))}
        </div>
      );
    });
  }, [
    analysisLayouts,
    computedGapBetweenPhases,
    pipelineAnalysisOutput.dimension,
    resultHasNextPhase,
    selectedLevel,
  ]);

  return (
    <div className="flex flex-col py-2 items-center relative" ref={measureRef}>
      {/* Left gradient - appears when scrolling right */}
      <div
        className="absolute top-0 left-0 h-full bg-gradient-to-l from-transparent to-white z-10"
        style={{
          width: `${leftGradientWidth}px`,
        }}
      ></div>
      {/* Right gradient - reduces when scrolling right */}
      <div
        className="absolute top-0 right-0 h-full bg-gradient-to-r from-transparent to-white z-10"
        style={{
          width: `${rightGradientWidth}px`,
        }}
      ></div>
      <div
        ref={scrollContainerRef}
        className="relative w-full overflow-x-auto"
        onScroll={handleScroll}
      >
        <div className="flex flex-row justify-between gap-6">
          {phaseLayoutsNodes}
        </div>
      </div>
    </div>
  );
};
